import { match, P } from 'ts-pattern';
import {
  BusinessStatus,
  OnboardingStatus,
  OnboardingTypeEnum,
} from '@Store/slices/customer/customer.types.ts';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { router } from '@Routes/__root.tsx';
import { routesModel } from '@Router/router.consts.ts';
import { RoutesMap } from '@Router/router.types.ts';
import { NAVIGATION_STACK_VERSION } from '@Types/consts.ts';
import { reArrangeNavigationStack } from '@Services/init/util.ts';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types';
import type { IPickUpWhreYouLeft } from '@Store/slices/customer/customer.types.ts';

interface IPickUpWhereLeftFunction {
  onboardingStatus?: OnboardingStatus;
  onboardingTypeEnum?: OnboardingTypeEnum;
  lastStepData?: IPickUpWhreYouLeft;
  nameTooLong?: boolean;
  onResume?: () => void;
}

export const handleLoginResponse = async ({
  onboardingStatus,
  onboardingTypeEnum,
  lastStepData,
  onResume,
}: IPickUpWhereLeftFunction) => {
  const { getCustomerData } = useBoundStore.getState();
  const { navigationStack, getIsEmbedded, onboardingFlowIdentifier } = useBoundStore
    .getState()
    .onboardingSlice.getOnboardingData();
  const { business, nameTooLong } = getCustomerData();

  const businessStatus = business?.status || undefined;

  const navigateToFirstPage = () => {
    if (
      routesModel[navigationStack[0] as keyof typeof routesModel].path ===
        routesModel[RoutesMap.WELCOME].path &&
      !getIsEmbedded() &&
      onboardingFlowIdentifier === OnboardingFlowIdentifier.DIRECT
    ) {
      router.navigate({ to: routesModel[navigationStack[1] as keyof typeof routesModel].path });
    } else {
      router.navigate({ to: routesModel[navigationStack[0] as keyof typeof routesModel].path });
    }
  };
  if (!onboardingStatus) {
    navigateToFirstPage();
    return;
  }
  const navigateToSpecificPage = (page: RoutesMap, searchParam?: object) => {
    router.navigate({ to: routesModel[page].path, search: searchParam });
  };

  const lastStepContinueWhereYouLeft = async (stepData: IPickUpWhreYouLeft) => {
    const { version, stepName } = stepData;
    if (version < NAVIGATION_STACK_VERSION) {
      navigateToFirstPage();
      if (onboardingStatus === OnboardingStatus.DURING_REGISTRATION && onResume) {
        onResume();
      }
      return;
    }
    await reArrangeNavigationStack(stepName);

    if (onboardingStatus === OnboardingStatus.DURING_REGISTRATION && onResume) {
      onResume();
    }
  };

  const isWhiteGlove = useBoundStore.getState().onboardingSlice.getIsWhiteGloveFlow();

  await match({
    businessStatus,
    onboardingStatus,
    onboardingTypeEnum,
    lastStepData,
    nameTooLong,
    isWhiteGlove,
  })
    .with(
      {
        nameTooLong: true,
        onboardingStatus: P.not(
          P.union(OnboardingStatus.KYC_FAILED_FINAL, OnboardingStatus.KYC_FAILED_PARTNER)
        ),
      },
      () => {
        navigateToSpecificPage(RoutesMap.DEBIT_CARD_NAME);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: OnboardingTypeEnum.MULTI_BUSINESS,
        lastStepData: undefined,
        isWhiteGlove: true,
      },
      () => {
        navigateToFirstPage();
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: OnboardingTypeEnum.MULTI_BUSINESS,
        lastStepData: undefined,
        isWhiteGlove: false,
      },
      () => {
        navigateToSpecificPage(RoutesMap.BUSINESS_TYPE_SELECTION);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: OnboardingTypeEnum.MULTI_BUSINESS,
        lastStepData: lastStepData,
      },
      async () => {
        await lastStepContinueWhereYouLeft(lastStepData!);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: OnboardingTypeEnum.ACCOUNTANT_AS_OWNER,
        lastStepData: undefined,
      },
      () => {
        navigateToFirstPage();
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: OnboardingTypeEnum.ACCOUNTANT_AS_OWNER,
        lastStepData: lastStepData,
      },
      async () => {
        await lastStepContinueWhereYouLeft(lastStepData!);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: OnboardingTypeEnum.NO_CONTINUE_BLOCK,
      },
      () => {
        navigateToSpecificPage(RoutesMap.KYC_FAILED);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        lastStepData: undefined,
      },
      () => {
        navigateToFirstPage();
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: OnboardingTypeEnum.STANDARD,
        lastStepData: lastStepData,
      },
      async () => {
        await lastStepContinueWhereYouLeft(lastStepData!);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.DURING_REGISTRATION,
        onboardingTypeEnum: undefined,
        lastStepData: lastStepData,
      },
      async () => {
        await lastStepContinueWhereYouLeft(lastStepData!);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: BusinessStatus.NEW,
      },
      () => {
        navigateToSpecificPage(RoutesMap.UNDER_REVIEW);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: BusinessStatus.RESUBMIT,
      },
      () => {
        navigateToSpecificPage(RoutesMap.UNDER_REVIEW);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: BusinessStatus.PENDING,
      },
      () => {
        navigateToSpecificPage(RoutesMap.KYB_PENDING);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: BusinessStatus.KYC_PENDING,
      },
      () => {
        navigateToSpecificPage(RoutesMap.UNDER_REVIEW);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: undefined,
      },
      () => {
        navigateToSpecificPage(RoutesMap.KYB_PENDING);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYB_PENDING,
        businessStatus: BusinessStatus.NONE,
      },
      () => {
        navigateToSpecificPage(RoutesMap.KYB_PENDING);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.COMPLETED,
      },
      () => {
        navigateToSpecificPage(RoutesMap.ONE_OF_US);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYC_FAILED_UPLOADED_DOCS,
      },
      () => {
        navigateToSpecificPage(RoutesMap.KYB_PENDING);
      }
    )
    .with(
      {
        onboardingStatus: OnboardingStatus.KYC_FIRST_FAILED,
      },
      () => {
        navigateToSpecificPage(RoutesMap.NON_US_SOCURE);
      }
    )
    .otherwise(() => {
      navigateToSpecificPage(RoutesMap.KYC_FAILED);
    });
};
